(() => {
  const root = document.body.dataset.root || '/';
  const url = path => root.replace(/\/?$/, '/') + path;
  const drawer = () => document.getElementById('CartDrawer');
  const mainCart = () => document.querySelector('[data-main-cart]');
  const refreshError = 'Your bag may have changed, but could not be refreshed. Open the full bag to review it before trying again.';
  const busyControls = new WeakMap();
  const pendingEditControls = new WeakMap();
  const externalMediaSources = new WeakMap();
  let focusBeforeCart = null;
  let focusAfterCartUpdate = null;
  let focusAfterMainCartUpdate = null;
  let pendingCartRequests = 0;
  let cartNeedsRefresh = false;
  let cartDismissals = 0;
  let queue = Promise.resolve();

  function status(message) {
    const element = document.querySelector('[data-global-status]');
    if (element) element.textContent = message;
  }

  function closeMenu(restoreFocus = false) {
    const menu = document.getElementById('MobileMenu');
    const toggle = document.querySelector('[data-menu-toggle]');
    if (!menu || menu.hidden) return;
    menu.hidden = true;
    toggle?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) toggle?.focus();
  }

  function openCart(trigger) {
    const dialog = drawer();
    if (!dialog || typeof dialog.showModal !== 'function') {
      location.assign(url('cart'));
      return;
    }
    closeMenu();
    if (!dialog.open) {
      focusBeforeCart = trigger || document.activeElement;
      dialog.showModal();
      dialog.querySelector('[data-cart-close]')?.focus();
    }
    document.body.classList.add('cart-is-open');
  }

  function restoreCartTrigger() {
    document.body.classList.remove('cart-is-open');
    focusAfterCartUpdate = null;
    const trigger = focusBeforeCart;
    focusBeforeCart = null;
    if (trigger?.isConnected && !trigger.disabled) trigger.focus();
    else document.querySelector('[data-cart-open]')?.focus();
  }

  function closeCart() {
    const dialog = drawer();
    if (!dialog?.open) return;
    cartDismissals += 1;
    dialog.close();
  }

  // The full bag uses a native Update form. Do not replace its unsaved values
  // with an offer or drawer response before the shopper has chosen to save them.
  function syncPendingCartEdits() {
    const dirty = Array.from(mainCart()?.querySelectorAll('[data-cart-quantity]') || [])
      .some(control => control.value !== control.defaultValue);
    [drawer(), mainCart()].filter(Boolean).forEach(scope => {
      const isMain = scope.matches('[data-main-cart]');
      const controls = isMain ? '[data-upsell-add]' : '[data-cart-quantity], [data-cart-remove], [data-upsell-select], [data-upsell-add], button[type="submit"], button[name="checkout"], button[name="update"]';
      scope.querySelectorAll(controls).forEach(control => {
        if (dirty) {
          if (!pendingEditControls.has(control)) pendingEditControls.set(control, busyControls.get(control) || { disabled: control.disabled, readOnly: control.readOnly, ariaDisabled: control.getAttribute('aria-disabled') });
          control.setAttribute('aria-disabled', 'true');
          if (control.matches('[data-cart-quantity]')) control.readOnly = true;
          else if ('disabled' in control) control.disabled = true;
        } else if (!pendingCartRequests && !cartNeedsRefresh && pendingEditControls.has(control)) {
          const original = pendingEditControls.get(control);
          if ('disabled' in control) control.disabled = original.disabled || !!control.dataset.retryBlocked;
          if ('readOnly' in control) control.readOnly = original.readOnly;
          if (original.ariaDisabled === null) control.removeAttribute('aria-disabled');
          else control.setAttribute('aria-disabled', original.ariaDisabled);
          pendingEditControls.delete(control);
        }
      });
      const message = isMain
        ? 'Select Update bag to save your quantity changes before adding another tee.'
        : 'Save your quantity changes in the full bag before continuing here.';
      const error = scope.querySelector('[data-cart-error]');
      if (!error) return;
      if (dirty && (!isMain || scope.querySelector('[data-upsell-add]'))) {
        if (error.textContent !== message) error.textContent = message;
        error.dataset.pendingCartEdits = message;
      } else if (error.dataset.pendingCartEdits) {
        if (error.textContent === error.dataset.pendingCartEdits) error.textContent = '';
        delete error.dataset.pendingCartEdits;
      }
    });
    return dirty;
  }

  // Lock mutation and checkout controls while retaining quantity-input focus.
  function syncCartBusy() {
    const busy = pendingCartRequests > 0 || cartNeedsRefresh;
    [drawer(), mainCart()].filter(Boolean).forEach(scope => {
      scope.toggleAttribute('aria-busy', pendingCartRequests > 0);
      if (pendingCartRequests > 0) scope.setAttribute('aria-busy', 'true');
      scope.querySelectorAll('[data-cart-quantity], [data-cart-remove], [data-upsell-select], [data-upsell-add], button[type="submit"], button[name="checkout"], button[name="update"]').forEach(control => {
        if (busy) {
          if (!busyControls.has(control)) {
            busyControls.set(control, { disabled: control.disabled, readOnly: control.readOnly, ariaDisabled: control.getAttribute('aria-disabled') });
          }
          control.setAttribute('aria-disabled', 'true');
          if (control.matches('[data-cart-quantity]')) control.readOnly = true;
          else if ('disabled' in control) control.disabled = true;
        } else {
          const original = busyControls.get(control);
          if (!original) return;
          if ('disabled' in control) control.disabled = original.disabled;
          if ('readOnly' in control) control.readOnly = original.readOnly;
          if (original.ariaDisabled === null) control.removeAttribute('aria-disabled');
          else control.setAttribute('aria-disabled', original.ariaDisabled);
          busyControls.delete(control);
        }
      });
    });
    syncPendingCartEdits();
  }

  function cartFocus(dialog) {
    const active = document.activeElement;
    if ((dialog === drawer() && !dialog.open) || !dialog.contains(active)) return null;
    return {
      id: active.id,
      key: active.dataset.lineKey,
      quantity: active.matches('[data-cart-quantity]'),
      remove: active.matches('[data-cart-remove]'),
      upsell: !!active.closest('[data-upsell-form]') || active.matches('[data-upsell-add]')
    };
  }

  function restoreCartFocus() {
    const fullCart = mainCart();
    const previousMain = focusAfterMainCartUpdate;
    focusAfterMainCartUpdate = null;
    if (previousMain && fullCart && !drawer()?.open && (document.activeElement === document.body || fullCart.contains(document.activeElement))) {
      const sameControl = previousMain.id && document.getElementById(previousMain.id);
      (sameControl && fullCart.contains(sameControl) ? sameControl : fullCart.querySelector('[name="checkout"]'))?.focus();
    }
    const dialog = drawer();
    const previous = focusAfterCartUpdate;
    focusAfterCartUpdate = null;
    if (!previous || !dialog?.open) return;
    if (document.activeElement !== document.body && !dialog.contains(document.activeElement)) return;
    let next;
    if (previous.key) {
      const selector = previous.quantity ? '[data-cart-quantity]' : '[data-cart-remove]';
      next = Array.from(dialog.querySelectorAll(selector)).find(control => control.dataset.lineKey === previous.key);
    } else if (previous.id) {
      const candidate = document.getElementById(previous.id);
      if (dialog.contains(candidate)) next = candidate;
    }
    (next || (previous.upsell && dialog.querySelector('[name="checkout"]')) || dialog.querySelector('[data-cart-quantity], [data-cart-close]'))?.focus();
  }

  async function refreshCart(sections) {
    const fullCart = mainCart();
    let nextMain;
    if (fullCart) {
      let mainHTML = sections?.[fullCart.dataset.sectionId];
      if (!mainHTML) {
        const response = await fetch(`${location.pathname}?section_id=${encodeURIComponent(fullCart.dataset.sectionId)}`, { headers: { Accept: 'text/html' }, cache: 'no-store' });
        if (!response.ok) throw new Error(refreshError);
        mainHTML = await response.text();
      }
      nextMain = new DOMParser().parseFromString(mainHTML, 'text/html').querySelector('[data-main-cart]');
      if (!nextMain) throw new Error(refreshError);
    }
    let html = sections?.['cart-drawer'];
    if (!html) {
      const response = await fetch(url('?section_id=cart-drawer'), { headers: { Accept: 'text/html' }, cache: 'no-store' });
      if (!response.ok) throw new Error(refreshError);
      html = await response.text();
    }
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer');
    const current = drawer();
    if (!next || !current) throw new Error(refreshError);
    focusAfterCartUpdate = cartFocus(current) || focusAfterCartUpdate;
    current.innerHTML = next.innerHTML;
    current.dataset.itemCount = next.dataset.itemCount || '0';
    if (fullCart) {
      focusAfterMainCartUpdate = cartFocus(fullCart) || focusAfterMainCartUpdate;
      fullCart.innerHTML = nextMain.innerHTML;
    }
    document.querySelectorAll('[data-cart-count]').forEach(element => { element.textContent = current.dataset.itemCount; });
    cartNeedsRefresh = false;
    status('');
    syncCartBusy();
  }

  function enqueue(task) {
    pendingCartRequests += 1;
    syncCartBusy();
    const pending = queue.catch(() => {}).then(task).finally(() => {
      pendingCartRequests -= 1;
      syncCartBusy();
      if (!pendingCartRequests) restoreCartFocus();
    });
    queue = pending;
    return pending;
  }

  function uncertainCartError(message) {
    const error = new Error(message);
    error.cartUncertain = true;
    return error;
  }

  async function requestCart(path, payload) {
    const headers = { Accept: 'application/json' };
    const sectionIds = ['cart-drawer'];
    if (mainCart()?.dataset.sectionId) sectionIds.push(mainCart().dataset.sectionId);
    let body;
    if (payload instanceof FormData) {
      payload.set('sections', sectionIds.join(','));
      payload.set('sections_url', location.pathname);
      body = payload;
    } else {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify({ ...payload, sections: sectionIds, sections_url: location.pathname });
    }
    let response;
    try {
      response = await fetch(url(path), { method: 'POST', headers, body });
    } catch {
      throw uncertainCartError('Your connection was interrupted. Open the full bag to review it before trying again.');
    }
    let data;
    try { data = await response.json(); }
    catch { throw uncertainCartError('Shopify could not return your bag. Please review the full bag before trying again.'); }
    if (!response.ok) {
      // Shopify can partially apply an inventory-limited request before returning an error.
      let reconciled = true;
      try { await refreshCart(data.sections); } catch { reconciled = false; }
      const message = data.description || data.message || 'Could not update your bag. Please try again.';
      throw reconciled ? new Error(message) : uncertainCartError(`${message} ${refreshError}`);
    }
    try { await refreshCart(data.sections); }
    catch { throw uncertainCartError(refreshError); }
    return data;
  }

  function failure(error, context) {
    const text = error?.message || 'Could not complete this request. Please try again.';
    const element = context?.querySelector('[data-product-error], [data-cart-error]');
    if (element) element.textContent = text;
    status(text);
  }

  function addSecondTee(button) {
    if (syncPendingCartEdits()) return;
    const scope = button.closest('#CartDrawer, [data-main-cart]');
    const form = button.closest('[data-upsell-form]');
    const select = (form || scope)?.querySelector('[data-upsell-select]');
    const option = select?.selectedOptions[0];
    if (!scope || pendingCartRequests || cartNeedsRefresh || button.disabled || button.dataset.retryBlocked || !option || option.disabled || !select.value) return;
    const variant = select.value;
    const fromMainCart = scope.matches('[data-main-cart]');
    if (fromMainCart) focusAfterMainCartUpdate = cartFocus(scope) || focusAfterMainCartUpdate;
    else focusAfterCartUpdate = cartFocus(scope) || focusAfterCartUpdate;
    const error = scope.querySelector('[data-cart-error]');
    if (error) error.textContent = '';
    enqueue(() => requestCart('cart/add.js', { items: [{ id: variant, quantity: 1 }] })).catch(error => {
      if (error.cartUncertain) {
        document.querySelectorAll('[data-upsell-add]').forEach(control => {
          control.disabled = true;
          control.dataset.retryBlocked = 'true';
        });
      }
      failure(error, fromMainCart ? mainCart() : drawer());
    });
  }

  function selectMedia(section, id) {
    if (!section || !id) return;
    const media = Array.from(section.querySelectorAll('[data-media-id]'));
    // A variant can point to an image intentionally omitted from this gallery.
    if (!media.some(element => element.dataset.mediaId === String(id))) return;
    media.forEach(element => {
      const wasHidden = element.hidden;
      element.hidden = element.dataset.mediaId !== String(id);
      if (element.hidden && !wasHidden) {
        element.querySelectorAll('video').forEach(video => video.pause());
      }
      // Unload hidden embedded players, including players configured to autoplay.
      element.querySelectorAll('iframe').forEach(frame => {
        if (element.hidden && frame.hasAttribute('src')) {
          externalMediaSources.set(frame, frame.getAttribute('src'));
          frame.removeAttribute('src');
        } else if (!element.hidden && externalMediaSources.has(frame)) {
          frame.setAttribute('src', externalMediaSources.get(frame));
          externalMediaSources.delete(frame);
        }
      });
    });
    section.querySelectorAll('[data-media-target]').forEach(element => {
      if (element.dataset.mediaTarget === String(id)) element.setAttribute('aria-current', 'true');
      else element.removeAttribute('aria-current');
    });
  }

  function updateVariant(select, updateUrl = true) {
    const option = select.selectedOptions[0];
    const section = select.closest('[data-product-section]');
    if (!option || !section) return;
    const form = select.closest('[data-product-form]');
    const button = form?.querySelector('[data-add-button]');
    const available = option.dataset.available === 'true';
    section.querySelectorAll('[data-variant-choice]').forEach(choice => {
      choice.setAttribute('aria-pressed', String(choice.dataset.variantChoice === select.value));
    });
    if (button) {
      button.disabled = !available || form.getAttribute('aria-busy') === 'true';
      const label = button.querySelector('[data-add-label]');
      if (label) label.textContent = available ? button.dataset.availableLabel : button.dataset.unavailableLabel;
    }
    const selectedSummary = section.querySelector('[data-selected-variant-value]');
    if (selectedSummary) selectedSummary.textContent = option.dataset.title || option.textContent.split(' — ')[0];
    const price = section.querySelector('[data-product-price]');
    if (price && option.dataset.price) price.textContent = option.dataset.price;
    if (option.dataset.media) selectMedia(section, option.dataset.media);
    if (updateUrl) {
      const next = new URL(location.href);
      next.searchParams.set('variant', select.value);
      history.replaceState(history.state, '', next);
    }
  }

  function initializeMedia(scope = document) {
    const sections = Array.from(scope.querySelectorAll('[data-product-section]'));
    if (scope instanceof Element && scope.matches('[data-product-section]')) sections.unshift(scope);
    sections.forEach(section => {
      const chips = section.querySelector('[data-variant-buttons]');
      if (chips) {
        chips.hidden = false;
        section.querySelector('[data-product-form]')?.classList.add('has-variant-chips');
      }
      const media = section.querySelector('[data-media-id]');
      if (media) selectMedia(section, media.dataset.mediaId);
      const select = section.querySelector('[data-variant-select]');
      if (select) updateVariant(select, false);
    });
  }

  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const toggle = target.closest('[data-menu-toggle]');
    if (toggle) {
      const menu = document.getElementById('MobileMenu');
      if (menu) {
        const expanded = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', String(expanded));
        menu.hidden = !expanded;
      }
      return;
    }
    if (target.closest('#MobileMenu a')) closeMenu();
    const cartOpen = target.closest('[data-cart-open]');
    if (cartOpen) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0 || !drawer()?.showModal) return;
      event.preventDefault();
      openCart(cartOpen);
      return;
    }
    if (target.closest('[data-cart-close]')) { event.preventDefault(); closeCart(); return; }
    if (target === drawer()) {
      const box = target.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) closeCart();
      return;
    }
    const remove = target.closest('[data-cart-remove]');
    if (remove && remove.closest('[data-main-cart]') && (pendingCartRequests || cartNeedsRefresh)) {
      event.preventDefault();
      return;
    }
    if (remove && remove.closest('#CartDrawer')) {
      event.preventDefault();
      if (pendingCartRequests || cartNeedsRefresh || syncPendingCartEdits() || !remove.dataset.lineKey) return;
      const key = remove.dataset.lineKey;
      enqueue(() => requestCart('cart/change.js', { id: key, quantity: 0 })).catch(error => failure(error, drawer()));
      return;
    }
    const upsell = target.closest('[data-upsell-add]');
    if (upsell && upsell.closest('#CartDrawer, [data-main-cart]')) {
      event.preventDefault();
      addSecondTee(upsell);
      return;
    }
    const choice = target.closest('[data-variant-choice]');
    if (choice) {
      if (choice.disabled) return;
      const select = choice.closest('[data-product-section]')?.querySelector('[data-variant-select]');
      if (select && Array.from(select.options).some(option => option.value === choice.dataset.variantChoice)) {
        select.value = choice.dataset.variantChoice;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }
    const fitLink = target.closest('[data-open-fit]');
    if (fitLink) {
      const href = fitLink.getAttribute('href');
      const guide = href?.startsWith('#') && document.getElementById(href.slice(1));
      if (guide) guide.open = true;
    }
    const mediaLink = target.closest('[data-media-target]');
    if (mediaLink && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) {
      event.preventDefault();
      selectMedia(mediaLink.closest('[data-product-section]'), mediaLink.dataset.mediaTarget);
    }
  });

  document.addEventListener('input', event => {
    if (event.target instanceof Element && event.target.matches('[data-cart-quantity]') && event.target.closest('[data-main-cart]')) syncPendingCartEdits();
  });

  document.addEventListener('change', event => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.matches('[data-cart-quantity]') && target.closest('[data-main-cart]')) syncPendingCartEdits();
    if (target.matches('[data-variant-select]')) updateVariant(target);
    if (target.matches('[data-upsell-select]')) {
      const price = target.closest('[data-upsell-form]')?.querySelector('[data-upsell-price]');
      const option = target.selectedOptions[0];
      if (price && option?.dataset.price) price.textContent = option.dataset.price;
    }
    if (target.matches('[data-cart-quantity]') && target.closest('#CartDrawer')) {
      if (pendingCartRequests || cartNeedsRefresh || syncPendingCartEdits()) return;
      if (!target.value.trim()) { target.value = target.defaultValue; return; }
      if (!target.reportValidity()) return;
      const quantity = Number(target.value);
      if (!Number.isInteger(quantity) || quantity < 0 || quantity > 99 || !target.dataset.lineKey) return;
      if (quantity === Number(target.defaultValue)) return;
      const key = target.dataset.lineKey;
      enqueue(() => requestCart('cart/change.js', { id: key, quantity })).catch(error => {
        if (target.isConnected) target.value = target.defaultValue;
        failure(error, drawer());
      });
    }
  });

  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.closest('#CartDrawer, [data-main-cart]') && (pendingCartRequests || cartNeedsRefresh)) { event.preventDefault(); return; }
    if (form.closest('#CartDrawer') && syncPendingCartEdits()) { event.preventDefault(); return; }
    if (form.matches('[data-upsell-form]') && form.closest('#CartDrawer, [data-main-cart]')) {
      event.preventDefault();
      const button = form.querySelector('[data-upsell-add]');
      if (button && form.reportValidity()) addSecondTee(button);
      return;
    }
    if (!form.matches('[data-product-form]') || !drawer()?.showModal) return;
    event.preventDefault();
    const button = form.querySelector('[data-add-button]');
    if (!button || button.disabled || form.getAttribute('aria-busy') === 'true' || !form.reportValidity()) return;
    const fields = new FormData(form);
    if (!fields.get('id')) return;
    const dismissalAtStart = cartDismissals;
    button.disabled = true;
    form.setAttribute('aria-busy', 'true');
    const error = form.querySelector('[data-product-error]');
    if (error) error.textContent = '';
    enqueue(() => requestCart('cart/add.js', fields)).then(() => {
      if (cartDismissals === dismissalAtStart) openCart(button);
    }).catch(error => failure(error, form)).finally(() => {
      form.removeAttribute('aria-busy');
      const select = form.querySelector('[data-variant-select]');
      if (select) updateVariant(select, false);
      else button.disabled = false;
    });
  });

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && !drawer()?.open) closeMenu(true);
  });
  document.addEventListener('cancel', event => {
    if (event.target?.id === 'CartDrawer' && !event.defaultPrevented) cartDismissals += 1;
  }, true);
  document.addEventListener('close', event => {
    if (event.target?.id === 'CartDrawer' && !event.target.open) restoreCartTrigger();
  }, true);
  document.addEventListener('shopify:section:load', event => initializeMedia(event.target));
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.contains(drawer())) document.body.classList.remove('cart-is-open');
  });
  if (typeof window.matchMedia === 'function') {
    const desktop = window.matchMedia('(min-width: 761px)');
    desktop.addEventListener?.('change', event => { if (event.matches) closeMenu(); });
  }
  window.addEventListener('pageshow', event => {
    closeMenu();
    const navigation = window.performance?.getEntriesByType?.('navigation')[0];
    if (!event.persisted && navigation?.type !== 'back_forward') return;
    // History may restore old DOM and form values after a different page changed
    // the cart. Reconcile with a read before any cached checkout can be submitted.
    cartNeedsRefresh = true;
    enqueue(() => refreshCart()).catch(() => {
      const error = new Error('Your bag could not be refreshed after returning to this page. Reload the page to review current quantities before continuing.');
      [mainCart(), drawer()].filter(Boolean).forEach(scope => failure(error, scope));
    });
  });
  initializeMedia();
})();
